/**
 * AuthContext — Supabase Auth state provider
 * Manages session, profile (role + team_name), and auth actions.
 */
import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { Sentry } from './sentry.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch profile from profiles table
  async function fetchProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, email, avatar_url, role, team_name')
      .eq('id', userId)
      .single();
    if (error) {
      console.error('[Auth] Profile fetch error:', error.message);
      return null;
    }
    return data;
  }

  // Auto-assign admin role if this is the very first user (platform admin)
  async function maybeAssignAdmin(userId) {
    const { count, error } = await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true });
    if (!error && count === 1) {
      // This is the only profile — make them platform admin
      await supabase.from('profiles').update({ role: 'admin' }).eq('id', userId);
      return 'admin';
    }
    return null;
  }

  useEffect(() => {
    let mounted = true;

    async function handleSession(session) {
      const u = session?.user ?? null;
      if (!mounted) return;
      setUser(u);
      if (u) {
        try {
          const p = await fetchProfile(u.id);
          if (p && !p.role) {
            const assigned = await maybeAssignAdmin(u.id);
            if (assigned) p.role = assigned;
          }
          if (mounted) setProfile(p);
          // Set Sentry user context for error attribution
          try {
            Sentry.setUser({ id: u.id, email: u.email, username: p?.display_name });
          } catch (_) { /* Sentry not available */ }
        } catch (e) {
          console.error('[Auth] Profile load failed:', e);
        }
      } else {
        setProfile(null);
        try { Sentry.setUser(null); } catch (_) { /* Sentry not available */ }
      }
      if (mounted) setLoading(false);
    }

    // Listen for auth changes (set up FIRST per Supabase v2 best practice)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => { handleSession(session); }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) console.error('[Auth] getSession error:', error.message);
      handleSession(session);
    }).catch(e => {
      console.error('[Auth] getSession failed:', e);
      if (mounted) setLoading(false);
    });

    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }

  async function signUp(email, password, displayName) {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName || email.split('@')[0] } },
    });
    return { error };
  }

  async function signInWithMagicLink(email) {
    const { error } = await supabase.auth.signInWithOtp({ email });
    return { error };
  }

  async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login?reset=true`,
    });
    return { error };
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  }

  // Refresh profile (e.g. after LM updates it)
  async function refreshProfile() {
    if (user) {
      const p = await fetchProfile(user.id);
      setProfile(p);
      return p;
    }
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signUp, signInWithMagicLink, resetPassword, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
